import { API_BASE_URL, API_TAG_TYPES, API_VERSION, JOB_MANAGER_API_HEADERS } from 'constants/common';
import { baseApi } from 'services/api';
import {
  AssignableMembersResponse,
  AutoFillJobDetailPayload,
  AutoFillJobDetailResponse,
  CheckJobAttriute,
  CreateJobExpensePayload,
  CreateJobExpenseResponse,
  CreateJobFromInvoicePayload,
  CreateJobPayload,
  CreateJobResponse,
  CreateNotePayload,
  CreateNoteResponse,
  DeleteJobExpensePayload,
  DeleteJobPayload,
  DeleteJobResponse,
  DeleteNotePayload,
  DeleteNoteResponse,
  EditJobAssigneesPayload,
  EditJobAssigneesResponse,
  EditJobDescriptionPayload,
  EditJobDescriptionResponse,
  EditJobDiscountPayload,
  EditJobExpensePayload,
  EditJobExternalCompanyPayload,
  EditJobExternalCompanyReferralPayload,
  EditJobExternalCompanyResponse,
  EditJobLeadSourcePayload,
  EditJobLeadSourceResponse,
  EditJobLineItemPayload,
  EditJobPayload,
  EditJobSchedulePayload,
  EditJobScheduleResponse,
  EditJobServiceAreaPayload,
  EditJobServiceAreaResponse,
  EditJobServiceChargePayload,
  EditJobStatusPayload,
  EditJobStatusResponse,
  EditNotePayload,
  EditNoteResponse,
  GetJobByIdPayload,
  GetJobByIdResponse,
  GetJobExpensesPayload,
  GetJobExpensesResponse,
  GetJobTimelinePayload,
  GetJobTimelineResponse,
  GetJobsInitPayload,
  GetJobsInitResponse,
  GetJobsPayload,
  GetJobsResponse,
  JobAttachmentListResponse,
  JobCommissionSplitResponse,
  JobRefNumberResponse,
  JobTeamIdPayload,
  ProfitabilitySummaryResponse,
  ReCalculateJobCommissionSplitPayload,
  ReCalculateJobCommissionSplitResponse,
  ServiceAreaSearchPayload,
  ServiceAreaSearchResponse,
  UnlinkJobAndInvoicePayload,
  UpdateJobAssigneesBulkPayload,
  UpdateJobAssigneesBulkResponse,
  UpdateJobCommissionSplitPayload,
  UpdateJobExpenseReceiptPayload,
  UpdateJobExpenseReceiptResponse,
  UpdateJobGroupByStatusPayload,
  UpdateJobGroupByStatusResponse,
  UpdateJobPaginationPayload,
  UpdateJobPaginationResponse,
  UpdateJobResponse,
  UpdateJobScheduleBulkPayload,
  UpdateJobScheduleBulkResponse,
  UpdateJobStatusBulkPayload,
  UpdateJobStatusBulkResponse,
  UpdateJobTagsBulkPayload,
  UpdateJobTagsPayload,
  UploadExpenseReceiptResponse,
  UploadImageResponse
} from './jobs.model';
import { FileTypes } from 'types/baseTypes';
import { downloadFile } from 'utils/commonUtils';
import {
  initialiseJobDetailsFromAPI,
  initialiseJobDetailsFromJob,
  initializeJobDetailsFromAutofill,
  updateIsInitialisingJobFromAPI
} from './create-edit-job/createJobSlice';
import { initialiseInvoiceSliceFromJob, updateIsInitialisingInvoiceSliceData } from 'containers/invoices/InvoicesSlice';
import { initializeEstimateFromJob, updateIsInitialisingEstimateFromAPI } from 'containers/estimates/estimatesSlice';
import {
  CreateViewRequest,
  TeamView,
  TeamViewsResponse,
  UpdateViewPriorityRequest,
  UpdateViewRequest,
  UpdateViewResponse
} from 'containers/dispatch/dispatch.model';
import { setJobsCurrentActiveView, setJobsCurrentViewFilters, setJobsInit } from './jobsSlice';
import { TeamIdPayload } from 'containers/home/home.model';
import { getJobViewFilters } from './utils';
import { Tag } from 'containers/tags/tags.model';
import { getUrlSearchParams, getUrlSearchParamsWithArrays } from 'utils/apiUtils';

const JobsBaseApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    createJob: builder.mutation<CreateJobResponse, CreateJobPayload>({
      query: ({ teamId, body }) => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V4}/merchants/teams/${teamId}/jobs`,
          method: 'POST',
          body,
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        failure: 'Failed to create job',
        showToast: true
      }
    }),
    getJobs: builder.query<GetJobsResponse, GetJobsPayload>({
      query: ({ teamId, ...params }) => {
        const url = `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/web/merchants/teams/${teamId}/jobs`;
        const urlParams = getUrlSearchParamsWithArrays(params);
        return {
          url: `${url}?${urlParams}`,
          method: 'GET',
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        failure: 'failed to fetch jobs data',
        showToast: true
      }
    }),
    exportJobs: builder.query<GetJobsResponse, GetJobsPayload>({
      query: params => {
        // eslint-disable-next-line max-len
        let url = `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${params.teamId}/jobs/export?exportDocType=${FileTypes.CSV}&`;
        const urlParams = new URLSearchParams();
        Object.keys(params).map(key => {
          if (Array.isArray(params[key])) {
            let filterParam = '';
            params[key].map(item => {
              filterParam = filterParam + `${item},`;
            });
            if (params[key].length > 0) urlParams.append(key, filterParam);
          } else {
            if (params[key] !== null) {
              urlParams.append(key, params[key]);
            }
          }
        });
        url = url + urlParams;
        return {
          url: url,
          method: 'GET',
          headers: JOB_MANAGER_API_HEADERS,
          responseHandler: async response => {
            await downloadFile({ response, fileType: FileTypes.CSV, fileName: 'jobs' });
          }
        };
      },
      extraOptions: {
        failure: 'failed to fetch jobs data',
        success: 'Jobs exported successfully!',
        showToast: true
      }
    }),
    getJobById: builder.query<GetJobByIdResponse, GetJobByIdPayload>({
      query: ({ jobId, teamId }) => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V2}/merchants/teams/${teamId}/jobs/${jobId}`,
          method: 'GET',
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      async onQueryStarted(
        { isFromEdit = false, isFromInvoice = false, isFromEstimate = false, isDuplicateJob = false },
        { dispatch, queryFulfilled }
      ) {
        if (isFromEdit || isFromInvoice || isFromEstimate || isDuplicateJob) {
          // Set initialization flags BEFORE the API call completes to prevent component interference
          if (isDuplicateJob || isFromEdit) {
            dispatch(updateIsInitialisingJobFromAPI(true));
          }

          if (isFromInvoice) {
            dispatch(updateIsInitialisingInvoiceSliceData(true));
          }

          if (isFromEstimate) {
            dispatch(updateIsInitialisingEstimateFromAPI(true));
          }

          const { data } = await queryFulfilled;

          if (isDuplicateJob) {
            dispatch(initialiseJobDetailsFromJob(data.data));
          }

          if (isFromEdit) {
            dispatch(initialiseJobDetailsFromAPI(data.data));
          }

          if (isFromInvoice) {
            dispatch(initialiseInvoiceSliceFromJob(data.data));
          }

          if (isFromEstimate) {
            dispatch(initializeEstimateFromJob(data.data));
          }
        }
      },
      providesTags: [API_TAG_TYPES.JOB_DETAILS],
      extraOptions: {
        failure: 'Failed to fetch job data'
      }
    }),
    uploadJobImageFile: builder.mutation<UploadImageResponse, { file: any; teamId: number }>({
      query: ({ teamId, file }) => {
        const formData = new FormData();
        formData.append('teamId', teamId.toString());
        formData.append('image', file);
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/attachment/upload `,
          method: 'POST',
          body: formData,
          headers: {
            ...JOB_MANAGER_API_HEADERS
            // make sure not to set the Content-Type header.
            // The browser will set it for you, including the boundary parameter.
            // 'Content-Type': 'multipart/form-data'
          }
        };
      }
    }),
    checkJobReferenceNumber: builder.mutation<JobRefNumberResponse, CheckJobAttriute>({
      query: ({ teamId, body }) => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/check`,
          method: 'POST',
          body,
          headers: JOB_MANAGER_API_HEADERS
        };
      }
    }),
    getAssignableMembers: builder.query<AssignableMembersResponse, string>({
      query: teamId => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/members`,
          headers: JOB_MANAGER_API_HEADERS
        };
      }
    }),
    editJobAssignees: builder.mutation<EditJobAssigneesResponse, EditJobAssigneesPayload>({
      query: ({ assignees, teamId, jobId }) => {
        const url = `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/assignee`;
        return {
          url: url,
          method: 'PATCH',
          body: {
            assignees
          },
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        failure: 'Failed to update job assignees',
        showToast: true
      },
      invalidatesTags: [API_TAG_TYPES.GET_PROFITABILITY_SUMMARY]
    }),
    editJobStatus: builder.mutation<EditJobStatusResponse, EditJobStatusPayload>({
      query: ({ status, note, teamId, jobId }) => {
        const url = `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V2}/merchants/teams/${teamId}/jobs/${jobId}/status`;
        return {
          url: url,
          method: 'PATCH',
          body: {
            status,
            note
          },
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        failure: 'Failed to update job status',
        showToast: true
      }
    }),
    editJobDescription: builder.mutation<EditJobDescriptionResponse, EditJobDescriptionPayload>({
      query: ({ description, teamId, jobId }) => {
        const url = `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/desc`;
        return {
          url: url,
          method: 'PATCH',
          body: {
            description
          },
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        failure: 'Failed to update job description',
        showToast: true
      }
    }),
    editJobLeadSource: builder.mutation<EditJobLeadSourceResponse, EditJobLeadSourcePayload>({
      query: ({ leadSourceType, leadSource, teamId, jobId }) => {
        const url = `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/lead-source`;
        return {
          url: url,
          method: 'PATCH',
          body: {
            leadSourceType,
            leadSource
          },
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        failure: 'Failed to update job lead source',
        showToast: true
      },
      invalidatesTags: [API_TAG_TYPES.GET_PROFITABILITY_SUMMARY]
    }),
    editJobSchedule: builder.mutation<EditJobScheduleResponse, EditJobSchedulePayload>({
      query: ({ scheduledFrom, scheduledTo, teamId, jobId }) => {
        const url = `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V2}/merchants/teams/${teamId}/jobs/${jobId}/schedule`;
        return {
          url: url,
          method: 'PATCH',
          body: {
            scheduledFrom,
            scheduledTo,
            isScheduled: Boolean(scheduledFrom && scheduledTo)
          },
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        failure: 'Failed to update job schedule',
        showToast: true
      }
    }),
    updateJobScheduleBulk: builder.mutation<UpdateJobScheduleBulkResponse, UpdateJobScheduleBulkPayload>({
      query: ({ teamId, body }) => {
        const url = `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/schedule/bulk`;
        return {
          url: url,
          method: 'PATCH',
          body,
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        failure: 'Failed to update job schedules',
        showToast: true
      }
    }),
    updateJobAssigneesBulk: builder.mutation<UpdateJobAssigneesBulkResponse, UpdateJobAssigneesBulkPayload>({
      query: ({ teamId, body }) => {
        const url = `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/assignee/bulk`;
        return {
          url: url,
          method: 'PATCH',
          body,
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        failure: 'Failed to update job assignees',
        showToast: true
      }
    }),
    getJobTimeline: builder.query<GetJobTimelineResponse, GetJobTimelinePayload>({
      query: ({ jobId, teamId, timezone }) => {
        return {
          // eslint-disable-next-line max-len
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/timeline?timezone=${timezone}`,
          method: 'GET',
          headers: JOB_MANAGER_API_HEADERS
        };
      },

      extraOptions: {
        showToast: false
      }
    }),
    createNote: builder.mutation<CreateNoteResponse, CreateNotePayload>({
      query: ({ teamId, jobId, attachments, comment }) => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/notes`,
          method: 'POST',
          body: {
            comment,
            attachments
          },
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        failure: 'Failed to add note',
        success: 'Note added successfully!',
        showToast: true
      }
    }),
    editNote: builder.mutation<EditNoteResponse, EditNotePayload>({
      query: ({ teamId, jobId, noteId, attachments, comment }) => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/notes/${noteId}`,
          method: 'PUT',
          body: {
            comment,
            attachments
          },
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        failure: 'Failed to update note',
        showToast: true
      }
    }),
    deleteNote: builder.mutation<DeleteNoteResponse, DeleteNotePayload>({
      query: ({ jobId, teamId, noteId }) => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/notes/${noteId}`,
          method: 'DELETE',
          headers: JOB_MANAGER_API_HEADERS
        };
      }
    }),
    editJob: builder.mutation<UpdateJobResponse, EditJobPayload>({
      query: ({ teamId, body, jobId }) => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V3}/merchants/teams/${teamId}/jobs/${jobId}`,
          method: 'PUT',
          body,
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        failure: 'Failed to edit job',
        showToast: true
      }
    }),
    createJobFromInvoice: builder.mutation<CreateJobResponse, CreateJobFromInvoicePayload>({
      query: ({ teamId, body, invoiceId }) => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V3}/merchants/teams/${teamId}/invoices/${invoiceId}/job`,
          method: 'POST',
          body,
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        failure: 'Failed to create job',
        showToast: true
      }
    }),
    unlinkJobAndInvoice: builder.mutation<DeleteNoteResponse, UnlinkJobAndInvoicePayload>({
      query: ({ jobId, teamId, invoiceId, sourceType }) => {
        return {
          // eslint-disable-next-line max-len
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/invoices/${invoiceId}?source=${sourceType}`,
          method: 'DELETE',
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      invalidatesTags: [API_TAG_TYPES.JOB_DETAILS, API_TAG_TYPES.GET_PROFITABILITY_SUMMARY]
    }),
    deleteJob: builder.mutation<DeleteJobResponse, DeleteJobPayload>({
      query: ({ jobId, teamId }) => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}`,
          method: 'DELETE',
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      invalidatesTags: [API_TAG_TYPES.GET_JOBS_FOR_MAP]
    }),
    getJobAttachmentList: builder.query<JobAttachmentListResponse, JobTeamIdPayload>({
      query: ({ teamId, jobId }) => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/attachments`,
          method: 'GET',
          headers: JOB_MANAGER_API_HEADERS
        };
      }
    }),
    autoFillJobDetail: builder.mutation<AutoFillJobDetailResponse, { teamId: string; body: AutoFillJobDetailPayload }>({
      query: ({ teamId, body }) => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V2}/merchants/teams/${teamId}/jobs/parse`,
          method: 'POST',
          body,
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(initializeJobDetailsFromAutofill(data.data));
        } catch (error) {
          // Error handling is already covered by extraOptions
        }
      },
      extraOptions: {
        failure: 'Failed to autofill job details',
        showToast: false
      }
    }),
    editExternalCompany: builder.mutation<EditJobExternalCompanyResponse, EditJobExternalCompanyPayload>({
      query: ({ externalCompanyId, teamId, jobId }) => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/external-company`,
          method: 'PATCH',
          body: {
            externalCompanyId
          },
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      invalidatesTags: [API_TAG_TYPES.JOB_DETAILS, API_TAG_TYPES.GET_PROFITABILITY_SUMMARY],
      extraOptions: {
        failure: 'Failed to update external company',
        showToast: true
      }
    }),
    getProfitabilitySummary: builder.query<ProfitabilitySummaryResponse, JobTeamIdPayload>({
      query: ({ teamId, jobId }) => {
        return {
          // eslint-disable-next-line max-len
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/profitability-summary`,
          method: 'GET',
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      providesTags: [API_TAG_TYPES.GET_PROFITABILITY_SUMMARY]
    }),
    getJobsTeamViews: builder.query<TeamViewsResponse, TeamIdPayload>({
      query: ({ teamId }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/teams/${teamId}/views?type=job`,
        method: 'GET',
        headers: JOB_MANAGER_API_HEADERS
      }),
      async onQueryStarted({ view: activeView, setNoFilters }, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data) {
          if (activeView) return;
          let activeTeamView = null;
          activeTeamView = data.data.find(view => view.isLastViewed);
          if (!activeTeamView) {
            activeTeamView = data.data.find(view => view.priority === 1);
          }
          const allJobsView = data.data.find(view => view.name === 'All jobs');
          if (setNoFilters) {
            // When default filters need not be applied, show all jobs view to show the jobs with state filters
            dispatch(setJobsCurrentActiveView(allJobsView));
          } else {
            dispatch(setJobsCurrentActiveView(activeTeamView));
            dispatch(setJobsCurrentViewFilters(getJobViewFilters(activeTeamView?.filters)));
          }
        }
      },
      providesTags: [API_TAG_TYPES.TEAM_VIEWS]
    }),
    createJobsView: builder.mutation<{ data: TeamView }, CreateViewRequest>({
      query: ({ teamId, body }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/teams/${teamId}/views`,
        method: 'POST',
        body,
        headers: JOB_MANAGER_API_HEADERS
      }),
      invalidatesTags: [API_TAG_TYPES.TEAM_VIEWS],
      extraOptions: {
        failure: 'Some error occurred, view not created',
        success: 'View created successfully',
        showToast: true
      }
    }),
    updateJobsView: builder.mutation<UpdateViewResponse, { teamId: string; viewId: string; body: UpdateViewRequest }>({
      query: ({ teamId, viewId, body }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/teams/${teamId}/views/${viewId}`,
        method: 'PUT',
        body: body,
        headers: JOB_MANAGER_API_HEADERS
      }),
      invalidatesTags: [API_TAG_TYPES.TEAM_VIEWS],
      extraOptions: {
        failure: 'Some error occurred, view not updated',
        success: 'View updated successfully',
        showToast: true
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data) {
          dispatch(setJobsCurrentActiveView(data.data));
        }
      }
    }),
    markJobsViewAsLastViewed: builder.mutation<void, { teamId: number; viewId: number }>({
      query: ({ teamId, viewId }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/teams/${teamId}/views/${viewId}/last-viewed?type=job`,
        method: 'PATCH',
        headers: JOB_MANAGER_API_HEADERS
      })
    }),
    updateJobsViewPriority: builder.mutation<void, UpdateViewPriorityRequest>({
      query: ({ teamId, body }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/teams/${teamId}/views/priority?type=job`,
        method: 'PUT',
        body,
        headers: JOB_MANAGER_API_HEADERS
      }),
      invalidatesTags: [API_TAG_TYPES.TEAM_VIEWS]
    }),
    deleteJobsView: builder.mutation<void, { teamId: number; viewId: number }>({
      query: ({ teamId, viewId }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/teams/${teamId}/views/${viewId}`,
        method: 'DELETE',
        headers: JOB_MANAGER_API_HEADERS
      }),
      extraOptions: {
        failure: 'Failed to delete view',
        success: 'View deleted successfully',
        showToast: true
      }
    }),
    renameJobsView: builder.mutation<
      { data: { viewId: number; name: string } },
      { teamId: number; viewId: number; name: string }
    >({
      query: ({ teamId, viewId, name }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/teams/${teamId}/views/${viewId}/name`,
        method: 'PATCH',
        body: { name },
        headers: JOB_MANAGER_API_HEADERS
      }),
      invalidatesTags: [API_TAG_TYPES.TEAM_VIEWS],
      extraOptions: {
        failure: 'Failed to rename view',
        success: 'View renamed successfully',
        showToast: true
      }
    }),
    editJobDiscount: builder.mutation<void, EditJobDiscountPayload>({
      query: ({ teamId, jobId, body }) => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/discount`,
          method: 'PATCH',
          body,
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        success: 'Discount updated successfully',
        failure: 'Failed to update discount',
        showToast: true
      },
      invalidatesTags: [API_TAG_TYPES.GET_PROFITABILITY_SUMMARY, API_TAG_TYPES.JOB_DETAILS]
    }),
    editJobServiceCharge: builder.mutation<void, EditJobServiceChargePayload>({
      query: ({ teamId, jobId, body }) => {
        return {
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/service-charge`,
          method: 'PUT',
          body,
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      extraOptions: {
        success: 'Service charge updated successfully',
        failure: 'Failed to update service charge',
        showToast: true
      },
      invalidatesTags: [API_TAG_TYPES.GET_PROFITABILITY_SUMMARY, API_TAG_TYPES.JOB_DETAILS]
    }),
    editJobLineItem: builder.mutation<void, EditJobLineItemPayload>({
      query: ({ teamId, jobId, body }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/line-item`,
        method: 'PUT',
        body,
        headers: JOB_MANAGER_API_HEADERS
      }),
      extraOptions: {
        success: 'Line item updated successfully',
        failure: 'Failed to update line items',
        showToast: true
      },
      invalidatesTags: [API_TAG_TYPES.GET_PROFITABILITY_SUMMARY, API_TAG_TYPES.JOB_DETAILS]
    }),
    updateJobTags: builder.mutation<void, UpdateJobTagsPayload>({
      query: ({ teamId, jobId, body }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/job/${jobId}/tags`,
        method: 'PUT',
        body,
        headers: JOB_MANAGER_API_HEADERS
      }),

      async onQueryStarted({ teamId, jobId, body }, { dispatch, queryFulfilled, getState }) {
        await queryFulfilled;
        const tagsQuery = getState().baseApi.queries[`getTags({"isActive":true,"teamId":${teamId}})`];
        const tagsMap = (tagsQuery?.data as { data: { tags: Tag[] } })?.data?.tags?.reduce((acc, tag) => {
          acc[tag.id] = tag;
          return acc;
        }, {});

        dispatch(
          JobsBaseApi.util.updateQueryData('getJobById', { isFromEdit: true, jobId, teamId }, draft => {
            if (draft) {
              draft.data.jobTags = body.tagIds?.map(tagId => tagsMap[tagId]).filter(Boolean);
            }
          })
        );
      },
      extraOptions: {
        failure: 'Failed to update tags',
        showToast: true
      }
    }),
    updateJobTagsBulk: builder.mutation<void, UpdateJobTagsBulkPayload>({
      query: ({ teamId, body }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/job/tags/bulk`,
        method: 'PUT',
        body,
        headers: JOB_MANAGER_API_HEADERS
      }),
      extraOptions: {
        failure: 'Failed to update job tags',
        showToast: true
      }
    }),
    updateJobStatusBulk: builder.mutation<UpdateJobStatusBulkResponse, UpdateJobStatusBulkPayload>({
      query: ({ teamId, body }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/status/bulk`,
        method: 'PATCH',
        body,
        headers: JOB_MANAGER_API_HEADERS
      }),
      extraOptions: {
        failure: 'Failed to update job statuses',
        showToast: true
      }
    }),
    getJobCommissionSplit: builder.query<JobCommissionSplitResponse, { teamId: number; jobId: string }>({
      query: ({ teamId, jobId }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/commission-split`,
        method: 'GET',
        headers: JOB_MANAGER_API_HEADERS
      })
    }),
    recalculateJobCommissionSplit: builder.mutation<
      ReCalculateJobCommissionSplitResponse,
      ReCalculateJobCommissionSplitPayload
    >({
      query: ({ teamId, body }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/commissions/re-calc`,
        method: 'POST',
        body
      }),
      extraOptions: {
        failure: 'Failed to recalculate commission split',
        showToast: true
      }
    }),
    updateJobCommissionSplit: builder.mutation<void, UpdateJobCommissionSplitPayload>({
      query: ({ teamId, jobId, body }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/commission-split`,
        method: 'PUT',
        body,
        headers: JOB_MANAGER_API_HEADERS
      }),
      invalidatesTags: [API_TAG_TYPES.GET_PROFITABILITY_SUMMARY, API_TAG_TYPES.PAY_RUN_MEMBER_JOBS],
      extraOptions: {
        success: 'Commission split updated successfully',
        failure: 'Failed to update commission split',
        showToast: true
      },
      async onQueryStarted({ teamId, jobId, commissionData }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          JobsBaseApi.util.updateQueryData('getJobCommissionSplit', { teamId, jobId }, draft => {
            draft.data = commissionData;
          })
        );
      }
    }),
    editJobExternalCompanyReferral: builder.mutation<void, EditJobExternalCompanyReferralPayload>({
      query: ({ teamId, jobId, amount, externalCompanyId, isEdited }) => ({
        // eslint-disable-next-line max-len
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/external-company-referral`,
        method: 'PATCH',
        body: {
          amount,
          externalCompanyId,
          isEdited
        },
        headers: JOB_MANAGER_API_HEADERS
      }),
      invalidatesTags: [
        API_TAG_TYPES.JOB_DETAILS,
        API_TAG_TYPES.GET_PROFITABILITY_SUMMARY,
        API_TAG_TYPES.PAY_RUN_MEMBER_JOBS
      ],
      extraOptions: {
        success: 'External company referral updated successfully',
        failure: 'Failed to update external company referral',
        showToast: true
      }
    }),
    deleteJobExternalCompany: builder.mutation<void, { teamId: number; jobId: string }>({
      query: ({ teamId, jobId }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/external-company`,
        method: 'DELETE',
        headers: JOB_MANAGER_API_HEADERS
      }),
      invalidatesTags: [
        API_TAG_TYPES.JOB_DETAILS,
        API_TAG_TYPES.PAY_RUN_MEMBER_JOBS,
        API_TAG_TYPES.GET_PROFITABILITY_SUMMARY
      ],
      extraOptions: {
        success: 'External company removed successfully',
        failure: 'Failed to remove external company',
        showToast: true
      }
    }),
    getJobExpenses: builder.query<GetJobExpensesResponse, GetJobExpensesPayload>({
      query: ({ teamId, jobId, ...params }) => {
        const searchParams = getUrlSearchParams(params);

        return {
          // eslint-disable-next-line max-len
          url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/expenses?${searchParams}`,
          method: 'GET',
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      providesTags: [API_TAG_TYPES.GET_JOB_EXPENSES]
    }),
    createJobExpense: builder.mutation<CreateJobExpenseResponse, CreateJobExpensePayload>({
      query: ({ teamId, jobId, body }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/expenses`,
        method: 'POST',
        body,
        headers: JOB_MANAGER_API_HEADERS
      }),
      extraOptions: {
        success: 'Expense created successfully!',
        failure: 'Failed to create expense',
        showToast: true
      },
      invalidatesTags: [
        API_TAG_TYPES.GET_PROFITABILITY_SUMMARY,
        API_TAG_TYPES.GET_JOB_EXPENSES,
        API_TAG_TYPES.PAY_RUN_MEMBER_JOBS,
        API_TAG_TYPES.JOB_DETAILS
      ]
    }),
    editJobExpense: builder.mutation<CreateJobExpenseResponse, EditJobExpensePayload>({
      query: ({ teamId, jobId, expenseId, body }) => ({
        // eslint-disable-next-line max-len
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/expenses/${expenseId}`,
        method: 'PUT',
        body,
        headers: JOB_MANAGER_API_HEADERS
      }),
      extraOptions: {
        success: 'Expense updated successfully!',
        failure: 'Failed to update expense',
        showToast: true
      },
      invalidatesTags: [
        API_TAG_TYPES.GET_PROFITABILITY_SUMMARY,
        API_TAG_TYPES.GET_JOB_EXPENSES,
        API_TAG_TYPES.EXPENSE_REPORT,
        API_TAG_TYPES.PAY_RUN_MEMBER_JOBS,
        API_TAG_TYPES.JOB_DETAILS
      ]
    }),
    deleteJobExpense: builder.mutation<void, DeleteJobExpensePayload>({
      query: ({ teamId, jobId, expenseId }) => ({
        // eslint-disable-next-line max-len
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/expenses/${expenseId}`,
        method: 'DELETE',
        headers: JOB_MANAGER_API_HEADERS
      }),
      extraOptions: {
        success: 'Expense deleted successfully!',
        failure: 'Failed to delete expense',
        showToast: true
      },
      invalidatesTags: [
        API_TAG_TYPES.GET_PROFITABILITY_SUMMARY,
        API_TAG_TYPES.PAY_RUN_MEMBER_JOBS,
        API_TAG_TYPES.JOB_DETAILS
      ]
    }),
    uploadExpenseReceipt: builder.mutation<UploadExpenseReceiptResponse, { teamId: number; jobId: string; file: File }>(
      {
        query: ({ teamId, jobId, file }) => {
          const formData = new FormData();
          formData.append('image', file);

          return {
            // eslint-disable-next-line max-len
            url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/expenses/receipt`,
            method: 'POST',
            body: formData,
            headers: JOB_MANAGER_API_HEADERS
          };
        }
      }
    ),
    updateJobExpenseReceipt: builder.mutation<UpdateJobExpenseReceiptResponse, UpdateJobExpenseReceiptPayload>({
      query: ({ teamId, jobId, expenseId, body }) => ({
        // eslint-disable-next-line max-len
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/expenses/${expenseId}/receipt`,
        method: 'PUT',
        body,
        headers: JOB_MANAGER_API_HEADERS
      }),
      extraOptions: {
        success: 'Expense receipt updated successfully!',
        failure: 'Failed to update expense receipt',
        showToast: true
      },
      invalidatesTags: [API_TAG_TYPES.EXPENSE_REPORT, API_TAG_TYPES.GET_JOB_EXPENSES]
    }),
    updateJobPaginationLimit: builder.mutation<UpdateJobPaginationResponse, UpdateJobPaginationPayload>({
      query: ({ teamId, pageSize }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V2}/merchants/teams/${teamId}/jobs/pagination`,
        method: 'PATCH',
        body: { pageSize },
        headers: JOB_MANAGER_API_HEADERS
      }),
      invalidatesTags: [API_TAG_TYPES.JOB_INIT],
      extraOptions: {
        success: 'Number of jobs per page updated successfully',
        failure: 'Failed to update number of jobs per page',
        showToast: true
      }
    }),
    toggleJobGroupByStatus: builder.mutation<UpdateJobGroupByStatusResponse, UpdateJobGroupByStatusPayload>({
      query: ({ teamId, groupByStatus }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V2}/merchants/teams/${teamId}/jobs/group-by-status`,
        method: 'PATCH',
        body: { groupByStatus },
        headers: JOB_MANAGER_API_HEADERS
      }),
      invalidatesTags: [API_TAG_TYPES.JOB_INIT],
      extraOptions: {
        success: 'Group by status updated successfully',
        failure: 'Failed to update group by status',
        showToast: false
      }
    }),
    getJobsInit: builder.query<GetJobsInitResponse, GetJobsInitPayload>({
      query: ({ teamId }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V2}/merchants/teams/${teamId}/jobs/init`,
        method: 'GET',
        headers: JOB_MANAGER_API_HEADERS
      }),
      providesTags: [API_TAG_TYPES.JOB_INIT],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.data) {
            dispatch(setJobsInit(data.data));
          }
        } catch (error) {
          // Handle error if needed
        }
      },

      extraOptions: {
        failure: 'Failed to fetch jobs init',
        showToast: true
      }
    }),
    findServiceAreas: builder.mutation<ServiceAreaSearchResponse, ServiceAreaSearchPayload>({
      query: ({ teamId, serviceAddressPoint }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/service-areas/find`,
        method: 'POST',
        body: { serviceAddressPoint }
      }),
      extraOptions: {
        failure: 'Failed to find service areas',
        showToast: true
      }
    }),
    editJobServiceArea: builder.mutation<EditJobServiceAreaResponse, EditJobServiceAreaPayload>({
      query: ({ teamId, jobId, serviceAreaId }) => ({
        url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/jobs/${jobId}/service-area`,
        method: 'PATCH',
        body: { serviceAreaId },
        headers: JOB_MANAGER_API_HEADERS
      }),
      extraOptions: {
        failure: 'Failed to update service area',
        showToast: true
      }
    })
  })
});

export const {
  useLazyGetJobsQuery,
  useLazyExportJobsQuery,
  useLazyGetJobByIdQuery,
  useLazyGetAssignableMembersQuery,
  useGetAssignableMembersQuery,
  useUploadJobImageFileMutation,
  useCheckJobReferenceNumberMutation,
  useCreateJobMutation,
  useEditJobAssigneesMutation,
  useEditJobStatusMutation,
  useEditJobDescriptionMutation,
  useEditJobLeadSourceMutation,
  useEditJobScheduleMutation,
  useUpdateJobScheduleBulkMutation,
  useUpdateJobAssigneesBulkMutation,
  useEditJobServiceAreaMutation,
  useLazyGetJobTimelineQuery,
  useCreateNoteMutation,
  useEditNoteMutation,
  useDeleteNoteMutation,
  useEditJobMutation,
  useCreateJobFromInvoiceMutation,
  useUnlinkJobAndInvoiceMutation,
  useDeleteJobMutation,
  useLazyGetJobAttachmentListQuery,
  useAutoFillJobDetailMutation,
  useEditExternalCompanyMutation,
  useLazyGetProfitabilitySummaryQuery,
  useGetJobsTeamViewsQuery,
  useLazyGetJobsTeamViewsQuery,
  useCreateJobsViewMutation,
  useUpdateJobsViewMutation,
  useMarkJobsViewAsLastViewedMutation,
  useUpdateJobsViewPriorityMutation,
  useDeleteJobsViewMutation,
  useRenameJobsViewMutation,
  useEditJobDiscountMutation,
  useEditJobServiceChargeMutation,
  useEditJobLineItemMutation,
  useUpdateJobTagsMutation,
  useUpdateJobTagsBulkMutation,
  useUpdateJobStatusBulkMutation,
  useGetJobByIdQuery,
  useLazyGetJobCommissionSplitQuery,
  useRecalculateJobCommissionSplitMutation,
  useUpdateJobCommissionSplitMutation,
  useEditJobExternalCompanyReferralMutation,
  useDeleteJobExternalCompanyMutation,
  useGetJobExpensesQuery,
  useLazyGetJobExpensesQuery,
  useCreateJobExpenseMutation,
  useEditJobExpenseMutation,
  useDeleteJobExpenseMutation,
  useUploadExpenseReceiptMutation,
  useUpdateJobExpenseReceiptMutation,
  useUpdateJobPaginationLimitMutation,
  useToggleJobGroupByStatusMutation,
  useLazyGetJobsInitQuery,
  useGetJobsInitQuery,
  useFindServiceAreasMutation
} = JobsBaseApi;
